import NurseRosterList from "@components/feature/rosterTable/row_NurseRosterList";
import { useEffect } from "react";
import type * as type from "@vTypes";
import { nurseGroupSortKey } from "@utils/common-util.ts";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";

type WantedTableNurse = Record<string, unknown> & {
  nurse_id?: string;
  id?: string;
  name?: string;
  experience?: number;
  role?: string;
  level?: string;
  off?: number;
  preceptor?: {
    color: string;
    ee: string[];
    group: { preceptor: string; preceptee: string[] } | number;
    groupIndex?: number;
    or: string[];
    role: string;
  } | null;
  weekly_off_weekday?: number | null;
  counts?: { [key: string]: number | null } | null;
};

type WeeklyOffDataLite = {
  items?: Array<{ nurse_id: string; preview_weekday?: number | null }>;
};

function TableBody_wanted({
  nurses,
  shiftInfo,
  currentSubmittedList,
  fullWanted,
  preferenceSchedule,
  assignmentsByNurse,
  isViewOnlyOff,
  currentYear,
  currentMonth,
  weeklyOffData,
  showWeeklyOff,
}: {
  nurses: WantedTableNurse[] | null;
  shiftInfo: type.availableShifts[];
  currentSubmittedList: string[];
  /** 현재 행 렌더에 미사용 — 상위에서 전달만 (타입은 Wanted/Preference 혼용) */
  fullWanted: unknown;
  preferenceSchedule: type.PreferenceSchedule[];
  assignmentsByNurse?: Map<string, InboundItem[]>;
  isViewOnlyOff: boolean;
  currentYear?: number;
  currentMonth?: number;
  weeklyOffData?: WeeklyOffDataLite;
  showWeeklyOff?: boolean;
}) {
  void fullWanted;
  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    shiftInfo,
    null
  );
  const noopModDayShift = () => {};

  useEffect(() => {
    const clickWindow = (e: Event) => {
      const clickTarget = e.target as HTMLElement;
      if (!clickTarget.closest(".preceptorMark")) {
        document
          .querySelectorAll(".preceptorGroup")
          .forEach((tr) => tr.classList.remove("click"));
      }
    };
    window.addEventListener("click", clickWindow);
    return () => {
      window.removeEventListener("click", clickWindow);
    };
  }, []);

  if (!nurses || !Array.isArray(nurses) || nurses.length === 0) {
    return (
      <div className="max-h-[60vh] px-[2px] py-[8px]">
        <div className="tbody_scollbox">
          <div className="text-center py-8 text-gray-500">
            표시할 간호사 데이터가 없습니다.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[60vh] px-[2px] py-[8px]">
      <div className="pb-10">
        {nurses.map((nurse, index) => {
          // nurse_id 확인 (nurse.nurse_id 또는 nurse.id)
          const nurseId = String(nurse.nurse_id ?? nurse.id ?? "");
          const nextNurse = nurses[index + 1];
          const roleGroupBottomBorder =
            nextNurse != null &&
            nurseGroupSortKey(nurse) !== nurseGroupSortKey(nextNurse);
          const isSubmitted = Array.isArray(currentSubmittedList)
            ? currentSubmittedList.includes(nurseId)
            : false;

          const find_preferenceShift = Array.isArray(preferenceSchedule)
            ? preferenceSchedule.find((p) => p.nurse_id === nurseId)?.schedule
            : null;
          const nurseAssignments =
            assignmentsByNurse?.get(String(nurseId)) ?? [];

          // 주휴 데이터 확인 (API 데이터 우선, 없으면 간호사 객체의 기본 설정 사용)
          const weeklyOffItem = weeklyOffData?.items?.find(
            (w) => w.nurse_id === nurseId
          );
          const weeklyOffDay =
            weeklyOffItem?.preview_weekday ?? nurse.weekly_off_weekday;

          // preferenceSchedule이 없을 때 날짜 수만큼 빈 배열 생성
          let preferenceShift: (string | null)[] = [];
          if (find_preferenceShift) {
            /** OFF 요청 현황 모달만 `O` 표시. 전체 현황은 `isViewOnlyOff={false}` 로 전부 표시. */
            preferenceShift = isViewOnlyOff
              ? find_preferenceShift.map((s) => (s === "O" ? "O" : null))
              : find_preferenceShift;
          } else if (currentYear && currentMonth) {
            // preferenceSchedule이 없을 때 날짜 수만큼 null로 채운 배열 생성
            const daysInMonth = new Date(
              currentYear,
              currentMonth,
              0
            ).getDate();
            preferenceShift = Array(daysInMonth).fill(null);
          }

          // 주휴 표시해야하는 경우 주휴 표시 병합
          if (
            showWeeklyOff &&
            weeklyOffDay !== undefined &&
            weeklyOffDay !== null &&
            currentYear &&
            currentMonth
          ) {
            preferenceShift = preferenceShift.map((shift, dayIdx) => {
              const day = dayIdx + 1;
              const date = new Date(currentYear, currentMonth - 1, day);
              // 월(0) ~ 일(6)
              const dayOfWeek = (date.getDay() + 6) % 7;

              if (dayOfWeek === weeklyOffDay) {
                // 주휴 우선 표시
                return "주";
              }
              return shift;
            });
          }

          return (
            <NurseRosterList
              key={String(nurseId)}
              isEditMode={false}
              nursesIndex={index}
              issuedRosterView={false}
              nurse={{
                counts: nurse.counts ?? null,
                experience: nurse.experience ?? 0,
                id: String(nurseId),
                name: nurse.name ?? "",
                preceptor: nurse.preceptor ?? null,
                schedule: preferenceShift.map((s) => s ?? "-"),
                off: nurse.off ?? 0,
                level: nurse.level ?? "",
                role: nurse.role ?? "",
                assignments: nurseAssignments,
              }}
              isSubmitted={isSubmitted}
              availableShifts={shiftInfo}
              shiftLookupMaps={shiftLookupMaps}
              modDayShift={noopModDayShift}
              selectedShift={null}
              scheduleYear={currentYear}
              scheduleMonth={currentMonth}
              schedulePayloadYear={currentYear}
              schedulePayloadMonth={currentMonth}
              showPerShiftStats={false}
              selectionFocusOnClick={false}
              roleGroupBottomBorder={roleGroupBottomBorder}
            />
          );
        })}
      </div>
    </div>
  );
}

export default TableBody_wanted;
